import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Dialog,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { formatDate } from "../../utils/deteUtils";
import { getDisaster } from "../../api";
import type { DisasterItem, DisasterKind } from "../../types/disaster";

type DisasterColumn = {
  id: keyof DisasterItem | string;
  label: string;
  minWidth: string;
  isSmallScreen: boolean;
  kind?: boolean;
  align?: "left" | "right" | "center" | "inherit" | "justify";
};

const columns: DisasterColumn[] = [
  {
    id: "start_at",
    label: "災害対応日時（始）",
    minWidth: "120px",
    isSmallScreen: true,
  },
  {
    id: "kind",
    label: "災害種別",
    minWidth: "120px",
    kind: true,
    isSmallScreen: false,
  },
  {
    id: "name",
    label: "災害名",
    minWidth: "120px",
    isSmallScreen: true,
  },
];

const disasterValidate = (disasterName?: DisasterKind) => {
  return (
    <Box
      sx={{
        borderRadius: "20px",
        padding: "8px",
        color: "white",
        fontWeight: "bold",
        width: "fit-content",
        backgroundColor:
          disasterName === "quake"
            ? "red"
            : disasterName === "w-r-s"
              ? "orange"
              : disasterName === "tsunami"
                ? "mediumblue"
                : disasterName === "manual"
                  ? "gray"
                  : "black",
      }}
    >
      {disasterName === "quake"
        ? "地震"
        : disasterName === "w-r-s"
          ? "雨風雪"
          : disasterName === "tsunami"
            ? "津波"
            : disasterName === "manual"
              ? "手動"
              : disasterName === "alert"
                ? "警報"
                : "その他"}
    </Box>
  );
};

export default function Disaster() {
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [disaster, setDisaster] = useState<DisasterItem | Record<string, never>>(
    {}
  );

  const getDisasterData = async () => {
    if (!disasterId) {
      return;
    }
    try {
      const response = await getDisaster(disasterId);
      if (response.status !== "ok") {
        return;
      }
      setDisaster(response.disaster);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  useEffect(() => {
    if (!disasterId) return;
    getDisasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disasterId]);

  const handleLocationCodeClick = () => {
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const getCellValue = (column: DisasterColumn): React.ReactNode => {
    if (column.kind) {
      return disasterValidate(disaster.kind);
    }
    if (column.id === "start_at") {
      return formatDate(disaster.start_at);
    }
    const value = disaster[column.id as keyof typeof disaster];
    return typeof value === "string" || typeof value === "number"
      ? value
      : "";
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    color: "outline.dark",
                    fontWeight: "bold",
                    borderBottom: 2,
                    padding: "8px",
                    "@media screen and (max-width:900px)": {
                      display:
                        column.isSmallScreen === true ? "table-cell" : "none",
                    },
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow
              hover
              role="checkbox"
              tabIndex={-1}
              onClick={() => handleLocationCodeClick()}
              sx={{
                pointerEvents: "none",
                "@media screen and (max-width:900px)": {
                  cursor: "pointer",
                  pointerEvents: "auto",
                },
              }}
            >
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  sx={{
                    padding: "8px",
                    border: "none",
                    "@media screen and (max-width:900px)": {
                      display:
                        column.isSmallScreen === true ? "table-cell" : "none",
                    },
                  }}
                  // @ts-expect-error legacy test attribute
                  name={`disasterData_${index}`}
                >
                  {getCellValue(column)}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
        <Box
          sx={{
            display: "flex",
            height: "56px",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px",
            borderBottom: "2px solid #CCC",
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              paddingLeft: "8px",
              fontWeight: "bold",
              fontSize: "20px",
            }}
          >
            災害情報詳細
          </Box>
          <IconButton
            aria-label="close"
            onClick={handlePopoverClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
          <TableBody>
            {columns.slice(0, 3).map((column) => (
              <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                <TableCell
                  align={column.align}
                  sx={{
                    color: "outline.dark",
                    fontWeight: "bold",
                    boxSizing: "border-box",
                    width: "calc((100vw - 64px) / 2)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      WebkitWritingMode: "horizontal-tb",
                      height: "100%",
                    }}
                  >
                    {column.label}
                  </Box>
                </TableCell>
                <TableCell
                  align={column.align}
                  sx={{
                    writingMode: "horizontal-tb",
                    boxSizing: "border-box",
                    width: "calc((100vw - 64px) / 2)",
                    overflowWrap: "break-word",
                  }}
                >
                  {getCellValue(column)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Dialog>
    </Paper>
  );
}
